import * as THREE from 'three';
import {
  EffectComposer,
  EffectPass,
  RenderPass,
  BloomEffect,
  VignetteEffect,
  SMAAEffect,
  BrightnessContrastEffect,
  HueSaturationEffect,
} from 'postprocessing';
import { Fencer } from './Fencer';
import { createVenue, pulseScoreLight } from './Piste';
import { CameraSystem } from './CameraSystem';
import { Input, KEYBIND_TABLE } from './Input';
import { RulesEngine, type TouchEvent } from './RulesEngine';
import { FencingAI } from './AI';
import { TutorialDirector } from './Tutorial';
import { GameAudio } from './Audio';
import { MobileControls } from './MobileControls';
import type { BoutMode, CameraMode, FencerColorId, GameMode } from './types';
import { FENCER_COLORS } from './types';
import type { CharacterId } from './characters';
import { setRuntimeModelUrl } from './characters';
import { animDebug } from './AnimDebug';
import { resolvePreferredModelUrl } from './EditorStorage';
import {
  SceneRuntime,
  FencerBodyBinder,
  createGameSingleton,
  getMainScenePath,
  loadProjectSettings,
  loadSceneByPath,
} from './scene';

export class Game {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly clock = new THREE.Clock();

  private composer: EffectComposer;
  private cameras: CameraSystem;
  private input: Input;
  private audio = new GameAudio();
  private mobile!: MobileControls;
  private player!: Fencer;
  private opponent!: Fencer;
  private venue!: THREE.Group;
  private rules = new RulesEngine(5, 180);
  private ai = new FencingAI(0.5, 0.6);
  private tutorial = new TutorialDirector();
  private mode: GameMode = 'menu';
  private boutMode: BoutMode = 'vs_ai';
  private pendingBoutMode: BoutMode = 'vs_ai';
  private colorP1: FencerColorId = 'blue';
  private colorP2: FencerColorId = 'red';
  private charP1: CharacterId = 'fencer';
  private charP2: CharacterId = 'fencer';
  /** Godot-style scene tree running alongside the bout (main scene scripts). */
  private sceneRuntime: SceneRuntime | null = null;
  private fencerBinder = new FencerBodyBinder();
  private scriptMove: { player: number; opponent: number } = { player: 0, opponent: 0 };
  private scriptActions: {
    player: { lunge: boolean; fleche: boolean; parry: 0 | 1 | -1; riposte: boolean };
    opponent: { lunge: boolean; fleche: boolean; parry: 0 | 1 | -1; riposte: boolean };
  } = {
    player: { lunge: false, fleche: false, parry: 0, riposte: false },
    opponent: { lunge: false, fleche: false, parry: 0, riposte: false },
  };
  private raf = 0;
  private sparkles: THREE.Points | null = null;
  private sparkleLife = 0;
  private bannerTimer = 0;
  private hitStop = 0;
  private envRT: THREE.PMREMGenerator;
  private menuOrbit = 0;
  private padPoll = 0;
  // DOM
  private el = {
    loading: document.getElementById('loading')!,
    progress: document.getElementById('progress-bar')!,
    menu: document.getElementById('menu')!,
    howto: document.getElementById('howto')!,
    colorSelect: document.getElementById('color-select')!,
    controlsModal: document.getElementById('controls-modal')!,
    hud: document.getElementById('hud')!,
    tutorial: document.getElementById('tutorial')!,
    pause: document.getElementById('pause')!,
    pauseTitle: document.getElementById('pause-title')!,
    pauseMsg: document.getElementById('pause-msg')!,
    p1Score: document.getElementById('p1-score')!,
    p2Score: document.getElementById('p2-score')!,
    p1Light: document.getElementById('p1-light')!,
    p2Light: document.getElementById('p2-light')!,
    p1Name: document.getElementById('p1-name')!,
    p2Name: document.getElementById('p2-name')!,
    time: document.getElementById('bout-time')!,
    priority: document.getElementById('priority-label')!,
    camBadge: document.getElementById('camera-badge')!,
    banner: document.getElementById('action-banner')!,
    hint: document.getElementById('hint-bar')!,
    tutTitle: document.getElementById('tut-title')!,
    tutText: document.getElementById('tut-text')!,
    tutBar: document.getElementById('tut-bar')!,
    flash: document.getElementById('touch-flash')!,
    period: document.getElementById('period')!,
    padStatus: document.getElementById('pad-status')!,
  };

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance',
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.cameras = new CameraSystem(window.innerWidth / window.innerHeight);
    this.input = new Input(canvas);
    this.mobile = new MobileControls(this.input, () => {
      if (this.mode === 'bout' || this.mode === 'tutorial') this.pause();
    });
    this.envRT = new THREE.PMREMGenerator(this.renderer);

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.cameras.camera));
    this.composer.addPass(
      new EffectPass(
        this.cameras.camera,
        new BloomEffect({
          intensity: 0.55,
          luminanceThreshold: 0.72,
          luminanceSmoothing: 0.25,
          mipmapBlur: true,
        }),
        new VignetteEffect({ darkness: 0.55, offset: 0.35 }),
        new BrightnessContrastEffect({ brightness: 0.02, contrast: 0.08 }),
        new HueSaturationEffect({ saturation: 0.08 }),
        new SMAAEffect(),
      ),
    );

    this.bindUI();
    window.addEventListener('resize', this.onResize);
  }

  async init(onProgress?: (n: number) => void): Promise<void> {
    onProgress?.(0.1);
    this.scene.background = new THREE.Color(0x05070c);
    this.scene.fog = new THREE.FogExp2(0x070b14, 0.028);

    // Lights
    const hemi = new THREE.HemisphereLight(0xb0c4ff, 0x1a120c, 0.55);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight(0xfff2e0, 2.1);
    key.position.set(4, 10, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 40;
    key.shadow.camera.left = -12;
    key.shadow.camera.right = 12;
    key.shadow.camera.top = 8;
    key.shadow.camera.bottom = -8;
    key.shadow.bias = -0.0002;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0x6688ff, 0.55);
    fill.position.set(-6, 4, -4);
    this.scene.add(fill);

    const rim = new THREE.SpotLight(0x6ee7ff, 40, 30, 0.5, 0.4, 1.2);
    rim.position.set(0, 8, 4);
    rim.target.position.set(0, 1, 0);
    this.scene.add(rim, rim.target);

    // Accent footlights
    const blueFoot = new THREE.PointLight(0x3d7bff, 8, 10, 2);
    blueFoot.position.set(-5, 0.4, 1.2);
    const redFoot = new THREE.PointLight(0xff4466, 8, 10, 2);
    redFoot.position.set(5, 0.4, 1.2);
    this.scene.add(blueFoot, redFoot);

    onProgress?.(0.35);

    // Environment map (procedural studio)
    const envScene = new THREE.Scene();
    envScene.add(new THREE.HemisphereLight(0xffffff, 0x444466, 1));
    const room = new THREE.Mesh(
      new THREE.BoxGeometry(20, 12, 20),
      new THREE.MeshBasicMaterial({ color: 0x1a2233, side: THREE.BackSide }),
    );
    envScene.add(room);
    const envTex = this.envRT.fromScene(envScene, 0.04).texture;
    this.scene.environment = envTex;

    onProgress?.(0.5);

    this.venue = createVenue();
    this.scene.add(this.venue);

    onProgress?.(0.52);
    await this.initSceneTree();

    onProgress?.(0.55);
    // Prefer Game Editor published / cloud-active model when available
    try {
      const url = await resolvePreferredModelUrl('/models/fencer-rigged.glb');
      if (url && url !== '/models/fencer-rigged.glb') setRuntimeModelUrl(url);
    } catch {
      /* keep default */
    }
    await this.rebuildFencers();

    onProgress?.(0.9);

    // Warm-up one frame
    this.cameras.update(0.016, this.player, this.opponent);
    this.composer.render(0.016);

    onProgress?.(1);
    await new Promise((r) => setTimeout(r, 150));

    this.el.loading.classList.add('hidden');
    this.el.menu.classList.remove('hidden');
    this.mode = 'menu';
    this.updatePadStatus();
    this.loop();

  }

  private bindUI(): void {
    const unlock = () => this.audio.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    document.getElementById('btn-tutorial')!.onclick = () => {
      this.audio.uiClick();
      this.startTutorial();
    };
    document.getElementById('btn-bout')!.onclick = () => {
      this.audio.uiClick();
      this.openColorSelect('vs_ai');
    };
    document.getElementById('btn-bout-2p')!.onclick = () => {
      this.audio.uiClick();
      this.openColorSelect('local_2p');
    };
    document.getElementById('btn-howto')!.onclick = () => {
      this.audio.uiClick();
      this.el.howto.classList.remove('hidden');
    };
    document.getElementById('howto-close')!.onclick = () => {
      this.audio.uiClick();
      this.el.howto.classList.add('hidden');
    };
    document.getElementById('btn-controls')!.onclick = () => {
      this.audio.uiClick();
      this.openControls();
    };
    document.getElementById('controls-close')!.onclick = () => {
      this.audio.uiClick();
      this.el.controlsModal.classList.add('hidden');
    };
    const editorBtn = document.getElementById('btn-editor');
    if (editorBtn) {
      editorBtn.onclick = () => {
        this.audio.uiClick();
        window.location.href = '/editor';
      };
    }
    document.getElementById('color-cancel')!.onclick = () => {
      this.audio.uiClick();
      this.el.colorSelect.classList.add('hidden');
      this.el.menu.classList.remove('hidden');
    };
    document.getElementById('color-start')!.onclick = async () => {
      this.audio.uiClick();
      const btn = document.getElementById('color-start') as HTMLButtonElement;
      btn.disabled = true;
      btn.textContent = 'Loading…';
      try {
        await this.rebuildFencers();
        this.el.colorSelect.classList.add('hidden');
        this.startBout(this.pendingBoutMode);
      } catch (e) {
        console.error(e);
        btn.textContent = 'Retry';
      } finally {
        btn.disabled = false;
        if (btn.textContent === 'Loading…') btn.textContent = 'Allez!';
      }
    };
    document.getElementById('btn-resume')!.onclick = () => this.resume();
    document.getElementById('btn-restart')!.onclick = () => {
      if (this.tutorial.active) this.startTutorial();
      else this.openColorSelect(this.boutMode);
    };
    document.getElementById('btn-quit')!.onclick = () => this.toMenu();
    document.getElementById('tut-next')!.onclick = () => {
      this.audio.uiClick();
      const r = this.tutorial.next();
      this.refreshTutorialUI();
      if (r === 'finished') this.openColorSelect('vs_ai');
    };
    document.getElementById('tut-skip')!.onclick = () => {
      this.audio.uiClick();
      this.tutorial.skip();
      this.el.tutorial.classList.add('hidden');
      this.openColorSelect('vs_ai');
    };

    this.buildSwatches();
    this.buildBindTable();
  }

  private async rebuildFencers(): Promise<void> {
    if (this.player?.root) this.scene.remove(this.player.root);
    if (this.opponent?.root) this.scene.remove(this.opponent.root);
    const [p1, p2] = await Promise.all([
      Fencer.create(true, this.colorP1, this.charP1),
      Fencer.create(false, this.colorP2, this.charP2),
    ]);
    this.player = p1;
    this.opponent = p2;
    this.scene.add(this.player.root, this.opponent.root);
    this.applySpawnMarkers();
    if (this.sceneRuntime) this.rebindFencersToScene();
  }

  /**
   * Load Project main scene into a SceneTree, register Game autoload,
   * hide duplicate fencer/piste visuals (venue + Fencer remain authoritative).
   */
  private async initSceneTree(): Promise<void> {
    const ps = loadProjectSettings();
    if (!ps.application.run_main_scene_in_game) return;
    const path = getMainScenePath();
    const doc = loadSceneByPath(path);
    if (!doc) {
      console.warn('[Game] Main scene not found:', path);
      return;
    }
    this.sceneRuntime = new SceneRuntime({
      log: (m) => console.debug('[scene]', m),
    });
    this.sceneRuntime.tree.register_singleton(
      'Game',
      createGameSingleton({
        getState: () => ({
          mode: this.mode,
          playerScore: this.rules.bout.playerScore,
          opponentScore: this.rules.bout.opponentScore,
          timeLeft: this.rules.bout.timeLeft,
          playerX: this.player?.state.x ?? 0,
          opponentX: this.opponent?.state.x ?? 0,
          priority: this.rules.bout.priority,
          boutMode: this.boutMode,
        }),
        setMoveIntent: (who, move) => {
          this.scriptMove[who] = THREE.MathUtils.clamp(move, -1, 1);
        },
        setActionIntent: (who, action) => {
          const slot = this.scriptActions[who];
          if (action === 'lunge') slot.lunge = true;
          else if (action === 'fleche') slot.fleche = true;
          else if (action === 'parry_high') slot.parry = 1;
          else if (action === 'parry_low') slot.parry = -1;
          else if (action === 'riposte') slot.riposte = true;
        },
        showBanner: (t) => this.showBanner(t),
        playSfx: (name) => {
          if (name === 'touch') this.audio.touchValid(true);
          else if (name === 'touch_off') this.audio.touchOff();
          else if (name === 'allez') this.audio.allez();
          else if (name === 'en_garde') this.audio.enGarde();
          else if (name === 'lunge') this.audio.lunge();
          else if (name === 'parry') this.audio.parry();
          else this.audio.uiClick();
        },
        setHint: (t) => this.setHint(t),
        pauseGame: () => this.pause(),
        getSpawn: (name) => this.sceneRuntime?.getMarkerPosition(name) ?? null,
      }),
    );
    this.sceneRuntime.logBind = (m) => console.info('[bind]', m);
    await this.sceneRuntime.build(doc);
    this.hideSceneEnvironmentDuplicates();
    this.scene.add(this.sceneRuntime.rootGroup);
    this.sceneRuntime.startPlay();
    this.rebindFencersToScene();
    console.info('[Game] SceneTree running', path);
  }

  /** Hide scene piste/floor duplicates; CharacterBodies stay for binding. */
  private hideSceneEnvironmentDuplicates(): void {
    if (!this.sceneRuntime) return;
    const root = this.sceneRuntime.rootGroup;
    root.traverse((o) => {
      const t = o.userData.sceneNodeType as string | undefined;
      const name = (o.name || '').toLowerCase();
      if (t === 'MeshInstance3D' && (name.includes('strip') || name.includes('piste') || name === 'floor')) {
        o.visible = false;
      }
      if (t === 'PackedScene' && name.includes('piste')) o.visible = false;
    });
  }

  /** Bind live Fencer actors to CharacterBody3D nodes (scripts/areas/debugger). */
  private rebindFencersToScene(): void {
    if (!this.sceneRuntime || !this.player || !this.opponent) return;
    this.fencerBinder.clear(this.sceneRuntime);
    this.fencerBinder.bind(this.sceneRuntime, {
      role: 'player',
      getX: () => this.player.state.x,
      setX: (x) => {
        this.player.state.x = x;
      },
      getFacing: () => this.player.state.facing,
      root: this.player.root,
    });
    this.fencerBinder.bind(this.sceneRuntime, {
      role: 'opponent',
      getX: () => this.opponent.state.x,
      setX: (x) => {
        this.opponent.state.x = x;
      },
      getFacing: () => this.opponent.state.facing,
      root: this.opponent.root,
    });
    this.fencerBinder.pushToBodies(this.sceneRuntime);
  }

  private applySpawnMarkers(): void {
    if (!this.player || !this.opponent || !this.sceneRuntime) return;
    const p1 = this.sceneRuntime.getMarkerPosition('EnGardeP1');
    const p2 = this.sceneRuntime.getMarkerPosition('EnGardeP2');
    if (p1) this.player.state.x = p1.x;
    if (p2) this.opponent.state.x = p2.x;
    this.fencerBinder.pushToBodies(this.sceneRuntime);
  }

  private consumeScriptActions(who: 'player' | 'opponent'): {
    lunge: boolean;
    fleche: boolean;
    parry: 0 | 1 | -1;
    riposte: boolean;
  } {
    const s = this.scriptActions[who];
    const out = { lunge: s.lunge, fleche: s.fleche, parry: s.parry, riposte: s.riposte };
    s.lunge = false;
    s.fleche = false;
    s.parry = 0;
    s.riposte = false;
    return out;
  }

  private buildSwatches(): void {
    const ids = Object.keys(FENCER_COLORS) as FencerColorId[];
    const fill = (rootId: string, which: 'p1' | 'p2') => {
      const root = document.getElementById(rootId)!;
      root.innerHTML = '';
      for (const id of ids) {
        const def = FENCER_COLORS[id];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'swatch';
        b.title = def.label;
        b.style.background = def.ui;
        b.dataset.color = id;
        b.onclick = () => {
          this.audio.uiClick();
          if (which === 'p1') this.colorP1 = id;
          else this.colorP2 = id;
          this.applyColors();
          this.syncSwatchSelection();
        };
        root.appendChild(b);
      }
    };
    fill('swatches-p1', 'p1');
    fill('swatches-p2', 'p2');
    this.syncSwatchSelection();
  }

  private syncSwatchSelection(): void {
    document.querySelectorAll('#swatches-p1 .swatch').forEach((el) => {
      el.classList.toggle('selected', (el as HTMLElement).dataset.color === this.colorP1);
    });
    document.querySelectorAll('#swatches-p2 .swatch').forEach((el) => {
      el.classList.toggle('selected', (el as HTMLElement).dataset.color === this.colorP2);
    });
  }

  private buildBindTable(): void {
    const tbody = document.querySelector('#bind-table tbody');
    if (!tbody) return;
    tbody.innerHTML = '';
    for (const row of KEYBIND_TABLE) {
      const tr = document.createElement('tr');
      tr.innerHTML = `<td>${row.action}</td><td>${row.p1}</td><td>${row.p2}</td><td>${row.pad}</td>`;
      tbody.appendChild(tr);
    }
  }

  private openControls(): void {
    this.updatePadStatus();
    const note = document.getElementById('controls-pad-note');
    if (note) {
      const n = this.input.gamepadCount();
      const touch = this.mobile.isTouchDevice();
      const parts: string[] = [];
      if (touch) {
        parts.push('Touch: virtual stick (move) + LUNGE / PARRY / FLÈCHE / RIP / CAM / pause.');
      }
      if (n === 0) {
        parts.push('No gamepad detected — plug one in anytime (P1 = first pad, P2 = second).');
      } else if (n === 1) {
        parts.push('1 gamepad connected — P1. Add a second pad for P2, or use keyboard for P2.');
      } else {
        parts.push(`${n} gamepads — pad 0 → P1, pad 1 → P2.`);
      }
      note.textContent = parts.join(' ');
    }
    this.el.controlsModal.classList.remove('hidden');
  }

  private openColorSelect(mode: BoutMode): void {
    this.pendingBoutMode = mode;
    this.el.menu.classList.add('hidden');
    this.el.pause.classList.add('hidden');
    const p1 = document.getElementById('color-p1-label');
    const p2 = document.getElementById('color-p2-label');
    if (p1) p1.textContent = 'Player 1 kit';
    if (p2) p2.textContent = mode === 'local_2p' ? 'Player 2 kit' : 'CPU kit';
    this.charP1 = 'fencer';
    this.charP2 = 'fencer';
    this.syncSwatchSelection();
    this.el.colorSelect.classList.remove('hidden');
  }

  private applyColors(): void {
    this.player?.setColor(this.colorP1);
    this.opponent?.setColor(this.colorP2);
  }

  private updatePadStatus(): void {
    if (!this.el.padStatus) return;
    const n = this.input.gamepadCount();
    const touch = this.mobile?.isTouchDevice() ? ' · Touch UI on' : '';
    this.el.padStatus.textContent =
      (n === 0 ? 'Controllers: none' : n === 1 ? 'Controllers: 1 pad (P1)' : `Controllers: ${n} pads`) +
      touch;
  }

  private startTutorial(): void {
    this.el.menu.classList.add('hidden');
    this.el.pause.classList.add('hidden');
    this.el.colorSelect.classList.add('hidden');
    this.el.controlsModal.classList.add('hidden');
    this.el.hud.classList.remove('hidden');
    this.el.tutorial.classList.remove('hidden');
    this.boutMode = 'vs_ai';
    this.applyColors();
    this.resetFencers();
    this.rules.reset(3, 600);
    this.rules.startFencing();
    this.ai.setProfile(0.25, 0.45);
    this.tutorial.start(this.player.state.x);
    this.mode = 'tutorial';
    this.el.period.textContent = 'ACADEMY';
    this.el.p1Name.textContent = 'YOU';
    this.el.p2Name.textContent = 'COACH';
    this.mobile.show();
    if (this.mobile.isTouchDevice()) this.cameras.setMode('side');
    this.refreshTutorialUI();
    this.showBanner('EN GARDE');
    this.audio.enGarde();
    this.setHint(
      this.mobile.isTouchDevice()
        ? 'Touch stick to move · LUNGE to attack · PARRY HI/LO to defend'
        : this.tutorial.step.hint ?? '',
    );
  }

  private startBout(mode: BoutMode = 'vs_ai'): void {
    this.boutMode = mode;
    this.el.menu.classList.add('hidden');
    this.el.pause.classList.add('hidden');
    this.el.colorSelect.classList.add('hidden');
    this.el.tutorial.classList.add('hidden');
    this.el.controlsModal.classList.add('hidden');
    this.el.hud.classList.remove('hidden');
    this.tutorial.stop();
    this.applyColors();
    this.resetFencers();
    this.rules.reset(5, 180);
    this.rules.startFencing();
    this.ai.setProfile(0.55, 0.65);
    this.mode = 'bout';
    this.el.period.textContent = mode === 'local_2p' ? '2P BOUT' : 'BOUT';
    this.el.p1Name.textContent = this.player.label.toUpperCase();
    this.el.p2Name.textContent =
      mode === 'local_2p' ? this.opponent.label.toUpperCase() : `CPU · ${this.opponent.label.toUpperCase()}`;
    // Touch pad always for P1 on mobile; 2P on phone is awkward — still show P1 pad + note
    if (mode === 'local_2p' || this.mobile.isTouchDevice()) this.cameras.setMode('side');
    this.mobile.show();
    this.onCameraChanged();
    this.showBanner('ALLEZ!');
    this.audio.allez();
    const touch = this.mobile.isTouchDevice();
    this.setHint(
      mode === 'local_2p'
        ? touch
          ? 'Touch pad = P1 · P2 needs keyboard/pad · First to 5'
          : 'P1: A/D keys · P2: Arrows · Pads auto-map · First to 5'
        : touch
          ? 'Stick to move · LUNGE · PARRY · FLÈCHE · First to 5'
          : 'Mouse aims sword · LMB attack · RMB parry · First to 5',
    );
    setTimeout(() => this.setHint(''), 4000);
  }

  private resetFencers(): void {
    this.player.state.x = -2.2;
    this.opponent.state.x = 2.2;
    this.applySpawnMarkers();
    this.player.state.facing = 1;
    this.opponent.state.facing = -1;
    this.player.state.action = 'idle';
    this.opponent.state.action = 'idle';
    this.player.state.energy = 1;
    this.opponent.state.energy = 1;
    this.player.state.hasPriority = false;
    this.opponent.state.hasPriority = false;
    this.player.state.canRiposte = false;
    this.opponent.state.canRiposte = false;
    this.scriptMove = { player: 0, opponent: 0 };
    pulseScoreLight(this.venue, 'L', false);
    pulseScoreLight(this.venue, 'R', false);
    pulseScoreLight(this.venue, 'W', false);
    this.updateHUD();
  }

  private toMenu(): void {
    this.mode = 'menu';
    this.tutorial.stop();
    this.el.hud.classList.add('hidden');
    this.el.tutorial.classList.add('hidden');
    this.el.pause.classList.add('hidden');
    this.el.colorSelect.classList.add('hidden');
    this.el.controlsModal.classList.add('hidden');
    this.mobile.hide();
    this.input.setWantPointerLock(false);
    document.getElementById('aim-reticle')?.classList.add('hidden');
    this.el.menu.classList.remove('hidden');
    this.updatePadStatus();
  }

  private resume(): void {
    this.el.pause.classList.add('hidden');
    this.mode = this.tutorial.active ? 'tutorial' : 'bout';
    if (this.mode === 'bout' || this.mode === 'tutorial') {
      this.mobile.show();
      this.onCameraChanged();
    }
  }

  private pause(title = 'Paused', msg = 'Take a breath. The strip waits.'): void {
    this.mode = 'paused';
    this.el.pauseTitle.textContent = title;
    this.el.pauseMsg.textContent = msg;
    this.el.pause.classList.remove('hidden');
    // Keep pad hidden under pause overlay so it doesn't steal taps
    this.mobile.hide();
    this.input.setWantPointerLock(false);
    document.getElementById('aim-reticle')?.classList.add('hidden');
  }

  private refreshTutorialUI(): void {
    const s = this.tutorial.step;
    this.el.tutTitle.textContent = s.title;
    this.el.tutText.textContent = s.text;
    this.el.tutBar.style.width = `${this.tutorial.progress}%`;
    this.setHint(s.hint ?? '');
    const nextBtn = document.getElementById('tut-next') as HTMLButtonElement;
    nextBtn.style.visibility = s.allowNext || !s.check ? 'visible' : 'hidden';
  }

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.05, this.clock.getDelta());
    this.tick(dt);
    this.composer.render(dt);
    this.input.endFrame();
    animDebug.tickUi();
  };

  private tick(dt: number): void {
    if (this.mode === 'editor') return;
    // Hit-stop freeze frames for impact readability
    if (this.hitStop > 0) {
      this.hitStop -= dt;
      this.cameras.update(dt, this.player, this.opponent);
      this.animateDust(dt);
      this.updateSparkles(dt);
      return;
    }

    // Idle animation on menu — slow orbit showcase
    if (this.mode === 'menu' || this.mode === 'paused' || this.mode === 'result') {
      this.sceneRuntime?.update(dt);
      if (this.sceneRuntime) this.fencerBinder.pushToBodies(this.sceneRuntime);
      this.menuOrbit += dt * 0.25;
      this.padPoll += dt;
      if (this.padPoll > 1) {
        this.padPoll = 0;
        this.updatePadStatus();
      }
      this.player.update(dt, 0, false, false, 0, false);
      this.opponent.update(dt, 0, false, false, 0, false);
      if (this.mode === 'menu') {
        this.player.state.x = -2.2 + Math.sin(this.menuOrbit) * 0.15;
        this.opponent.state.x = 2.2 - Math.sin(this.menuOrbit * 0.9) * 0.12;
      }
      this.cameras.update(dt, this.player, this.opponent);
      this.animateDust(dt);
      return;
    }

    // Camera switches mid-game
    if (this.input.cycleCamera()) {
      this.cameras.cycle();
      this.onCameraChanged();
    }
    const camD = this.input.cameraDigit();
    if (camD === 1) this.setCam('side');
    if (camD === 2) this.setCam('third');
    if (camD === 3) this.setCam('fps');

    // Mouse: aim always + LMB attack / RMB parry (P1, not 2P keyboard fight)
    this.updateMouseAim(dt);
    if (this.boutMode !== 'local_2p' && !this.mobile.isTouchDevice()) {
      if (this.input.mouseAttack()) {
        if (this.player.playAttack()) this.tutorial.noteLunge();
      }
      if (this.input.mouseParry()) {
        if (this.player.playParryFromAim()) this.tutorial.noteParry();
      }
    }

    if (this.input.pause()) {
      this.pause();
      return;
    }

    // SceneTree scripts (Godot main scene) — may set Game.set_move / action
    this.sceneRuntime?.update(dt);
    // Script move_and_slide on bound CharacterBody → fencer strip X
    if (this.sceneRuntime) this.fencerBinder.pullFromBodies(this.sceneRuntime);

    const p1 = this.input.sample(0);
    const p1Script = this.consumeScriptActions('player');
    // Blend: keyboard/pad wins if non-zero, else scene-script intent
    if (Math.abs(p1.move) < 0.05 && Math.abs(this.scriptMove.player) > 0.01) {
      p1.move = this.scriptMove.player;
    }
    p1.lunge = p1.lunge || p1Script.lunge;
    p1.fleche = p1.fleche || p1Script.fleche;
    p1.riposte = p1.riposte || p1Script.riposte;
    if (p1.parry === 0 && p1Script.parry !== 0) p1.parry = p1Script.parry;

    if (p1.move > 0.1) this.tutorial.noteAdvance();
    if (p1.move < -0.1) this.tutorial.noteRetreat();
    if (p1.lunge || p1.fleche) this.tutorial.noteLunge();
    if (p1.parry !== 0) this.tutorial.noteParry();

    // Player facing toward opponent
    this.player.state.facing = this.player.state.x <= this.opponent.state.x ? 1 : -1;
    this.opponent.state.facing = this.opponent.state.x < this.player.state.x ? 1 : -1;

    this.player.update(dt, p1.move, p1.lunge, p1.fleche, p1.parry, p1.riposte);
    this.sfxFromFencer(this.player);

    // P2 human / AI / tutorial coach
    const freezeOpp = this.mode === 'tutorial' && !!this.tutorial.step.freezeOpp;
    if (freezeOpp) {
      this.opponent.update(dt, 0, false, false, 0, false);
    } else if (this.boutMode === 'local_2p' && this.mode === 'bout') {
      const p2 = this.input.sample(1);
      const p2Script = this.consumeScriptActions('opponent');
      if (Math.abs(p2.move) < 0.05 && Math.abs(this.scriptMove.opponent) > 0.01) {
        p2.move = this.scriptMove.opponent;
      }
      p2.lunge = p2.lunge || p2Script.lunge;
      p2.fleche = p2.fleche || p2Script.fleche;
      p2.riposte = p2.riposte || p2Script.riposte;
      if (p2.parry === 0 && p2Script.parry !== 0) p2.parry = p2Script.parry;
      this.opponent.update(dt, p2.move, p2.lunge, p2.fleche, p2.parry, p2.riposte);
    } else if (this.mode === 'tutorial' && this.tutorial.step.id === 'parry') {
      this.ai.setProfile(0.7, 0.3);
      this.ai.update(dt, this.opponent, this.player);
    } else {
      // AI bout — still allow scene AI script to bias opponent move
      const oppScript = this.consumeScriptActions('opponent');
      if (Math.abs(this.scriptMove.opponent) > 0.15) {
        this.opponent.update(
          dt,
          this.scriptMove.opponent,
          oppScript.lunge,
          oppScript.fleche,
          oppScript.parry,
          oppScript.riposte,
        );
      } else {
        this.ai.update(dt, this.opponent, this.player);
      }
    }
    this.sfxFromFencer(this.opponent);
    // Mirror combat positions onto scene CharacterBody3D (areas, debugger, scripts)
    if (this.sceneRuntime) this.fencerBinder.pushToBodies(this.sceneRuntime);
    // Decay continuous script move each frame so stale intents don't stick
    this.scriptMove.player *= 0.85;
    this.scriptMove.opponent *= 0.85;

    // Keep separation
    const minSep = 0.55;
    const sep = this.opponent.state.x - this.player.state.x;
    if (Math.abs(sep) < minSep) {
      const mid = (this.player.state.x + this.opponent.state.x) / 2;
      this.player.state.x = mid - Math.sign(sep || 1) * minSep * 0.5;
      this.opponent.state.x = mid + Math.sign(sep || 1) * minSep * 0.5;
    }

    const freezeClock = this.mode === 'tutorial';
    const touch = this.rules.update(dt, this.player, this.opponent, freezeClock);
    if (touch) this.onTouch(touch);

    if (this.mode === 'tutorial') {
      const r = this.tutorial.update(dt);
      if (r === 'advanced') this.refreshTutorialUI();
      if (r === 'finished') {
        this.showBanner('ACADEMY COMPLETE');
        setTimeout(() => this.startBout(), 1200);
      }
    }

    if (this.rules.bout.phase === 'end' && this.mode === 'bout') {
      this.mode = 'result';
      const p1s = this.rules.bout.playerScore;
      const p2s = this.rules.bout.opponentScore;
      const p1Wins = p1s > p2s;
      const draw = p1s === p2s;
      if (draw) this.audio.halt();
      else if (p1Wins || this.boutMode === 'local_2p') this.audio.win();
      else this.audio.lose();
      if (this.boutMode === 'local_2p') {
        this.pause(
          draw ? 'Draw' : p1Wins ? 'Player 1 Wins' : 'Player 2 Wins',
          `Final ${p1s}–${p2s}. Excellent fencing.`,
        );
      } else {
        this.pause(
          p1Wins ? 'Victory' : 'Defeat',
          p1Wins
            ? `You win ${p1s}–${p2s}. Beautiful fencing.`
            : `Final ${p1s}–${p2s}. Reset and take the strip again.`,
        );
      }
    }

    this.cameras.update(dt, this.player, this.opponent);
    this.updateHUD();
    this.animateDust(dt);
    this.updateSparkles(dt);

    if (this.bannerTimer > 0) {
      this.bannerTimer -= dt;
      if (this.bannerTimer <= 0) this.el.banner.classList.remove('show');
    }
  }

  private setCam(mode: CameraMode): void {
    this.cameras.setMode(mode);
    this.onCameraChanged();
  }

  private onCameraChanged(): void {
    this.el.camBadge.textContent = this.cameras.label();
    this.tutorial.noteCamera();
    this.audio.cameraSwitch();
    // Want lock in all camera modes for sword aim (desktop 1P)
    this.input.setWantPointerLock(
      this.boutMode !== 'local_2p' &&
        !this.mobile.isTouchDevice() &&
        (this.mode === 'bout' || this.mode === 'tutorial'),
    );
    this.updateReticle();
  }

  /**
   * Desktop: mouse steers blade in all directions (all camera modes).
   * Pointer-lock free-look preferred; otherwise follow cursor.
   * LMB / RMB handled in tick via input.mouseAttack / mouseParry.
   */
  private updateMouseAim(dt: number): void {
    if (this.boutMode === 'local_2p' || this.mobile.isTouchDevice()) {
      this.updateReticle();
      return;
    }

    const reticle = document.getElementById('aim-reticle');
    reticle?.classList.remove('hidden');
    const sens =
      this.cameras.mode === 'fps' ? 0.0048 : this.cameras.mode === 'third' ? 0.004 : 0.0032;

    if (this.input.pointerLocked) {
      this.player.state.bladeAngle = THREE.MathUtils.clamp(
        this.player.state.bladeAngle - this.input.mouseDX * sens,
        -1.35,
        1.35,
      );
      this.player.state.stance = THREE.MathUtils.clamp(
        this.player.state.stance - this.input.mouseDY * sens,
        -1.15,
        1.15,
      );
    } else {
      const targetYaw = this.input.mouseNX * 1.15;
      const targetPitch = this.input.mouseNY * 0.95;
      const k = 1 - Math.pow(0.0005, dt);
      this.player.state.bladeAngle = THREE.MathUtils.lerp(this.player.state.bladeAngle, targetYaw, k);
      this.player.state.stance = THREE.MathUtils.lerp(this.player.state.stance, targetPitch, k);
    }

    if (this.boutMode === 'vs_ai' && this.mode === 'bout') {
      this.opponent.state.bladeAngle = Math.sin(performance.now() * 0.0013) * 0.18;
      this.opponent.state.stance = Math.sin(performance.now() * 0.001) * 0.1;
    }
    this.updateReticle();
  }

  private updateReticle(): void {
    const reticle = document.getElementById('aim-reticle');
    if (!reticle) return;
    const show =
      (this.mode === 'bout' || this.mode === 'tutorial') &&
      this.boutMode !== 'local_2p' &&
      !this.mobile.isTouchDevice();
    reticle.classList.toggle('hidden', !show);
    reticle.classList.toggle('locked', this.input.pointerLocked);
    const hint = document.getElementById('aim-hint');
    if (hint) {
      hint.textContent = this.input.pointerLocked
        ? 'LMB attack · RMB parry · move mouse to aim'
        : 'Click to lock · LMB attack · RMB parry · mouse aims sword';
    }
  }

  private sfxFromFencer(f: Fencer): void {
    if (f.footstepPulse) this.audio.footstep();
    const started = f.consumeJustStarted();
    if (!started) return;
    if (started === 'lunge' || started === 'fleche' || started === 'riposte') this.audio.lunge();
    if (started === 'parryHigh' || started === 'parryLow') this.audio.parry();
  }

  private onTouch(ev: TouchEvent): void {
    this.hitStop = ev.valid ? 0.12 : 0.06;
    this.audio.halt();

    pulseScoreLight(this.venue, 'L', false);
    pulseScoreLight(this.venue, 'R', false);
    pulseScoreLight(this.venue, 'W', false);

    this.el.p1Light.className = 'light';
    this.el.p2Light.className = 'light';
    this.el.flash.className = 'touch-flash';

    if (ev.simultaneous && ev.valid) {
      this.showBanner('DOUBLE TOUCH');
      pulseScoreLight(this.venue, 'L', true);
      pulseScoreLight(this.venue, 'R', true);
      this.el.p1Light.classList.add('on-valid');
      this.el.p2Light.classList.add('on-red');
      this.el.flash.classList.add('show-green');
      this.cameras.addShake(0.12);
      this.spawnSparks(this.player.tipWorld);
      this.spawnSparks(this.opponent.tipWorld);
      this.audio.touchValid(true);
      this.audio.touchValid(false);
    } else if (ev.valid && ev.by === 'player') {
      this.showBanner('TOUCHÉ!');
      pulseScoreLight(this.venue, 'L', true);
      this.el.p1Light.classList.add('on-valid');
      this.el.flash.classList.add('show-green');
      this.cameras.addShake(0.18);
      this.spawnSparks(this.opponent.lameCenter);
      this.tutorial.noteScore();
      this.audio.touchValid(true);
    } else if (ev.valid && ev.by === 'opponent') {
      this.showBanner('TOUCHÉ');
      pulseScoreLight(this.venue, 'R', true);
      this.el.p2Light.classList.add('on-red');
      this.el.flash.classList.add('show-red');
      this.cameras.addShake(0.18);
      this.spawnSparks(this.player.lameCenter);
      this.audio.touchValid(false);
    } else if (!ev.valid) {
      this.showBanner(ev.zone === 'floor' ? 'FLOOR' : 'OFF TARGET');
      pulseScoreLight(this.venue, 'W', true);
      this.el.flash.classList.add('show-white');
      this.el.p1Light.classList.add('on-invalid');
      this.el.p2Light.classList.add('on-invalid');
      this.audio.touchOff();
    }

    setTimeout(() => {
      this.el.flash.className = 'touch-flash hidden';
      this.el.p1Light.className = 'light';
      this.el.p2Light.className = 'light';
      pulseScoreLight(this.venue, 'L', false);
      pulseScoreLight(this.venue, 'R', false);
      pulseScoreLight(this.venue, 'W', false);
    }, 900);
  }

  private spawnSparks(at: THREE.Vector3): void {
    if (this.sparkles) {
      this.scene.remove(this.sparkles);
      this.sparkles.geometry.dispose();
    }
    const n = 48;
    const pos = new Float32Array(n * 3);
    const vel: number[] = [];
    for (let i = 0; i < n; i++) {
      pos[i * 3] = at.x;
      pos[i * 3 + 1] = at.y;
      pos[i * 3 + 2] = at.z;
      vel.push(
        (Math.random() - 0.5) * 3,
        Math.random() * 2.5,
        (Math.random() - 0.5) * 3,
      );
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    (geo as THREE.BufferGeometry & { userData: { vel: number[] } }).userData = { vel };
    this.sparkles = new THREE.Points(
      geo,
      new THREE.PointsMaterial({
        color: 0x9fefff,
        size: 0.06,
        transparent: true,
        opacity: 1,
        depthWrite: false,
      }),
    );
    this.scene.add(this.sparkles);
    this.sparkleLife = 0.55;
  }

  private updateSparkles(dt: number): void {
    if (!this.sparkles || this.sparkleLife <= 0) return;
    this.sparkleLife -= dt;
    const pos = this.sparkles.geometry.attributes.position as THREE.BufferAttribute;
    const vel = (this.sparkles.geometry as THREE.BufferGeometry & { userData: { vel: number[] } })
      .userData.vel;
    for (let i = 0; i < pos.count; i++) {
      pos.setX(i, pos.getX(i) + vel[i * 3] * dt);
      pos.setY(i, pos.getY(i) + vel[i * 3 + 1] * dt);
      pos.setZ(i, pos.getZ(i) + vel[i * 3 + 2] * dt);
      vel[i * 3 + 1] -= 4 * dt;
    }
    pos.needsUpdate = true;
    const mat = this.sparkles.material as THREE.PointsMaterial;
    mat.opacity = Math.max(0, this.sparkleLife * 2);
    if (this.sparkleLife <= 0) {
      this.scene.remove(this.sparkles);
      this.sparkles = null;
    }
  }

  private animateDust(dt: number): void {
    const dust = this.venue.getObjectByName('dust') as THREE.Points | undefined;
    if (!dust) return;
    dust.rotation.y += dt * 0.02;
  }

  private updateHUD(): void {
    this.el.p1Score.textContent = String(this.rules.bout.playerScore);
    this.el.p2Score.textContent = String(this.rules.bout.opponentScore);
    this.el.time.textContent = this.rules.formatTime();
    this.el.priority.textContent = this.rules.priorityLabel();
    this.el.camBadge.textContent = this.cameras.label();
    const p1e = document.getElementById('p1-energy');
    const p2e = document.getElementById('p2-energy');
    if (p1e) p1e.style.transform = `scaleX(${this.player.state.energy.toFixed(3)})`;
    if (p2e) p2e.style.transform = `scaleX(${this.opponent.state.energy.toFixed(3)})`;
  }

  private showBanner(text: string): void {
    this.el.banner.textContent = text;
    this.el.banner.classList.add('show');
    this.bannerTimer = 1.1;
  }

  private setHint(text: string): void {
    if (!text) {
      this.el.hint.classList.remove('show');
      this.el.hint.textContent = '';
      return;
    }
    this.el.hint.textContent = text;
    this.el.hint.classList.add('show');
  }

  private onResize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.cameras.resize(w / h);
    this.composer.setSize(w, h);
  };

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.mobile.dispose();
    this.input.dispose();
    animDebug.dispose();
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
  }
}
